/**
 * Pure helpers for the Platform admin console's Policies tab
 * (components/PolicySettingsPanel.jsx) over /admin/policy-settings
 * (docs/Decisions.md D53). The server is the validator
 * (src/shared/security/policy-settings.js validatePolicy); this file turns its
 * sparse layers into form rows and back.
 *
 * A layer is sparse: a field that is absent inherits from the layer below
 * (org -> platform -> built-in default). In the form every field has an
 * `inherit` flag, and only fields with `inherit: false` are sent.
 */

/** The rows the tab shows, in order, with how each is edited. */
export const POLICY_ROWS = [
  {
    section: 'donations',
    field: 'minAmountCents',
    label: 'Smallest donation',
    kind: 'money',
    hint: 'Per gift. An organization can set a higher minimum of its own.',
  },
  {
    section: 'donations',
    field: 'maxAmountCents',
    label: 'Largest donation',
    kind: 'money',
    hint: 'Per gift. An organization can set a lower maximum of its own.',
  },
  {
    section: 'donations',
    field: 'requireSignIn',
    label: 'Donors must sign in to a registered account',
    kind: 'bool',
  },
  {
    section: 'donations',
    field: 'platformFeePercent',
    label: 'Platform fee (%)',
    kind: 'percent',
    orgOnly: true,
    hint: "Replaces this organization's service tier rate. The default for every organization is the tier rate, set in Service Tiers.",
  },
  {
    section: 'donations',
    field: 'maxPlatformFeePercent',
    label: 'Most the platform fee can be (%)',
    kind: 'percent',
    hint: 'A tier rate or an organization override above this is charged at this cap.',
  },
  {
    section: 'donations',
    field: 'maxCombinedFeePercent',
    label: 'Most the platform and organization fees can be together (%)',
    kind: 'percent',
    hint: "An organization's own fee can be at most this, less its platform fee, and never more than 10.",
  },
  {
    section: 'marketplace',
    field: 'platformFeePercent',
    label: 'Platform fee (% of the organization fee)',
    kind: 'percent',
    hint: 'Taken from the organization fee on each won deal, so it moves with what the organization charges.',
  },
  {
    section: 'marketplace',
    field: 'platformFeeMinCents',
    label: 'Smallest platform fee',
    kind: 'money',
    hint: 'Per won deal; never more than the organization fee itself.',
  },
  {
    section: 'marketplace',
    field: 'platformFeeMaxCents',
    label: 'Largest platform fee',
    kind: 'money',
    hint: 'Per won deal.',
  },
  {
    section: 'marketplace',
    field: 'maxLeadFeeCents',
    label: 'Largest fixed lead fee',
    kind: 'money',
    hint: 'The most an organization can set as a fixed lead fee for a category.',
  },
  {
    section: 'marketplace',
    field: 'maxLeadFeePercent',
    label: 'Largest lead fee (% of the deal value)',
    kind: 'percent',
  },
  {
    section: 'marketplace',
    field: 'maxOrgFeeCents',
    label: 'Largest fixed organization fee',
    kind: 'money',
  },
  {
    section: 'marketplace',
    field: 'maxOrgFeePercent',
    label: 'Largest organization fee (% of the lead fee)',
    kind: 'percent',
  },
  {
    section: 'marketplace',
    field: 'maxRewardPercent',
    label: 'Largest recommender reward (% of the lead fee)',
    kind: 'percent',
    hint: 'What a vendor can offer people who recommend it.',
  },
  {
    section: 'sites',
    field: 'retainedVersions',
    label: 'Site versions kept',
    kind: 'int',
    hint: 'The live version plus the most recent others. Older uploads are deleted automatically.',
  },
  {
    section: 'ribbons',
    field: 'eventsCountAt',
    label: 'Event ribbons count an event at',
    kind: 'enum',
    options: [
      ['check-in', 'Check-in (attended)'],
      ['registration', 'Registration'],
    ],
  },
];

/** Rows that apply at a level: the platform level has no org-only rows. */
export function rowsFor(level) {
  return POLICY_ROWS.filter(row => level === 'org' || !row.orgOnly);
}

const centsToDollars = cents => (cents / 100).toFixed(2);

/** A value as the form edits it (always a string for text inputs). */
function toInput(row, value) {
  if (value === null || value === undefined) return row.kind === 'bool' ? false : '';
  if (row.kind === 'money') return centsToDollars(value);
  if (row.kind === 'bool') return Boolean(value);
  return String(value);
}

/** A value as it is shown when inherited. */
export function displayValue(row, value) {
  if (value === null || value === undefined) {
    return row.field === 'platformFeePercent' ? 'Service tier rate' : 'Not set';
  }
  switch (row.kind) {
    case 'money':
      return `$${Number(centsToDollars(value)).toLocaleString('en-US', { minimumFractionDigits: 2 })} USD`;
    case 'bool':
      return value ? 'Yes' : 'No';
    case 'percent':
      return `${value}%`;
    case 'enum':
      return (row.options.find(([v]) => v === value) || [value, value])[1];
    default:
      return String(value);
  }
}

/**
 * Form state for one level. `inherited` is what each field would be without
 * this layer (for the platform: the built-in defaults; for an org: the
 * platform default as resolved).
 */
export function layerToForm(layer, inherited, level) {
  const form = {};
  for (const row of rowsFor(level)) {
    const key = `${row.section}.${row.field}`;
    const own = layer?.[row.section]?.[row.field];
    const fallback = inherited?.[row.section]?.[row.field];
    const has = own !== undefined && own !== null;
    form[key] = { inherit: !has, value: toInput(row, has ? own : fallback) };
  }
  return form;
}

function fromInput(row, value) {
  switch (row.kind) {
    case 'money':
      return Math.round(Number(value) * 100);
    case 'int':
      return Number(value);
    case 'percent':
      return Number(value);
    case 'bool':
      return Boolean(value);
    default:
      return value;
  }
}

/** Form state -> the sparse layer the PUT sends. */
export function formToLayer(form, level) {
  const layer = {};
  for (const row of rowsFor(level)) {
    const entry = form[`${row.section}.${row.field}`];
    if (!entry || entry.inherit) continue;
    layer[row.section] = layer[row.section] || {};
    layer[row.section][row.field] = fromInput(row, entry.value);
  }
  return layer;
}

/** Client-side checks that mirror the server's bounds (the server re-checks). */
export function policyFormErrors(form, fields, level) {
  const errors = [];
  for (const row of rowsFor(level)) {
    const entry = form[`${row.section}.${row.field}`];
    if (!entry || entry.inherit || row.kind === 'bool' || row.kind === 'enum') continue;
    const spec = fields?.[row.section]?.[row.field];
    const raw = String(entry.value).trim();
    const n = Number(raw);
    if (raw === '' || !Number.isFinite(n)) {
      errors.push(`${row.label}: enter a number.`);
      continue;
    }
    if (!spec) continue;
    const stored = fromInput(row, raw);
    if (row.kind === 'int' && !Number.isInteger(stored)) {
      errors.push(`${row.label}: enter a whole number.`);
    } else if (row.kind === 'money' && Math.abs(n * 100 - Math.round(n * 100)) > 1e-6) {
      errors.push(`${row.label}: use whole cents.`);
    } else if (stored < spec.min || stored > spec.max) {
      const lo = row.kind === 'money' ? displayValue(row, spec.min) : spec.min;
      const hi = row.kind === 'money' ? displayValue(row, spec.max) : spec.max;
      errors.push(`${row.label}: must be from ${lo} to ${hi}.`);
    }
  }
  return errors;
}

/** Where a resolved value came from, in words. */
export function sourceLabel(source) {
  return (
    { default: 'Built-in default', platform: 'Platform default', org: 'This organization' }[
      source
    ] || source
  );
}
